import React, { useEffect, useState } from 'react'
import { Link, Redirect, useParams, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { toast } from 'react-toastify'
import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'
import * as yup from 'yup'
import { Helmet } from 'react-helmet'
import * as qs from 'querystring'
import ProductMenuItemRelationsForm from '../ProductMenuItemRelationsForm'
import { MODE_EDIT } from '../ProductMenuItemRelationsForm'
import { getProductName } from '../../Products/util'

const ProductMenuItemRelationsEditContainer = ({ parentProduct, children }) => {
    const { id } = useParams()
    const query = useLocation()

    const queryParams = query.search ? qs.parse(query.search.slice(1)) : null

    return (
        <div className="form-container">
            <Heading
                name={
                    parentProduct
                        ? `Edit Product Menu Item Relation for ${parentProduct.name} (${parentProduct.sku})`
                        : 'Loading...'
                }
            >
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary !whitespace-nowrap"
                        to={
                            queryParams?.to
                                ? `/${queryParams.to}`
                                : `/products/${id}/menu-item-relations`
                        }
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Product Menu Item Relations
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const ProductMenuItemRelationsEdit = () => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(false)
    const { id, relationId } = useParams()

    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState()
    const [parentProduct, setParentProduct] = useState()
    const [menuItemRelation, setMenuItemRelation] = useState({
        displayOrder: 0,
        menuItems: '',
        label: '',
    })

    const pathname = `/products/${id}/menu-item-relations/${relationId}`

    const fetchProductOptions = async () => {
        setLoading(true)
        try {
            const { data: productData, response } = await client(GET_ONE, 'products', {
                id: `/products/${id}`,
            })

            setParentProduct({ sku: productData.sku, name: getProductName(productData) })
            setLoading(false)

            if (response.status === 404) {
                setRedirect(`/products/${id}/menu-item-relations`)
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect(`/products/${id}/menu-item-relations`)
        }
    }

    const fetchProductMenuItemRelation = async () => {
        setLoading(true)

        try {
            const { data: menuItemRelation } = await client(
                GET_ONE,
                'product-menu-item-relations',
                {
                    id: `/product-menu-item-relations/${relationId}`,
                }
            )

            setMenuItemRelation({
                displayOrder: menuItemRelation?.displayOrder || 0,
                menuItems: `/menu-items/${menuItemRelation?.menuItems?.id}` || '',
                label: menuItemRelation?.label || '',
            })

            setLoading(false)
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect(`/products/${id}/menu-item-relations`)
        }
    }

    useEffect(() => {
        fetchProductOptions()
        fetchProductMenuItemRelation()
    }, [pathname])

    const schema = getSchemaFromResource(getResource('menu-item-relations')).shape({
        menuItems: yup.string().required(),
        label: yup.string().required(),
    })

    const onSubmit = async (data, actions) => {
        try {
            const response = await client(UPDATE, 'product-menu-item-relations', {
                id: `/product-menu-item-relations/${relationId}`,
                data: {
                    ...data,
                    displayOrder: typeof data.displayOrder === 'number' ? data.displayOrder : 0,
                },
            })

            if (response?.data?.originId) {
                setRedirect(`/products/${id}/menu-item-relations/${response.data.originId}`)
            } else {
                setRedirect(`/products/${id}/menu-item-relations`)
            }

            toast.success('Menu Item Relation successfully updated!')
            actions.setSubmitting(false)
        } catch (e) {
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            actions.setValues(data)
            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <ProductMenuItemRelationsEditContainer parentProduct={parentProduct}>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </ProductMenuItemRelationsEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url={`/products/${id}/menu-item-relations`}
                message="The product menu item relation you're looking for could not be found"
                buttonLabel="Go to Product Menu Item Relations"
            />
        )
    }

    return (
        <ProductMenuItemRelationsEditContainer parentProduct={parentProduct}>
            {redirect && <Redirect push to={redirect} />}
            <Helmet>
                <title>Product Menu Item Relation | Edit | Peracto</title>
            </Helmet>

            <ProductMenuItemRelationsForm
                onSubmit={onSubmit}
                mode={MODE_EDIT}
                schema={schema}
                values={menuItemRelation}
            />
        </ProductMenuItemRelationsEditContainer>
    )
}

export default ProductMenuItemRelationsEdit
